/**
 * STEP (ISO 10303-21, AP214) export of the 3D model the dollhouse view renders.
 *
 * Consumes the export scene (SceneSolids, as prisms, plus SceneWalls) lowered
 * to planar boundary faces by prismFaces and wallBodies, and welds each
 * solid's faces into one manifold B-rep shell (stepSolid). Each wall leaf is
 * a body whose windows and doors are real holes (hole loops in the wall
 * faces, tunnel reveals through the thickness), and door and window segments
 * become named child products, so importers show them as folders.
 *
 * Millimeters (declared in the file's unit context), room centered at the
 * origin, z-up: the same frame as the STL export. Each body is named after
 * its semantic layer (wall, door, glass, …) for importers that show body names.
 */

import { FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import { buildScene3D } from './buildScene3d';
import { type ExportScene, toCenteredMillimeters } from './planarFace';
import { prismFaces, scenePrisms } from './prismFaces';
import { stepSolid } from './stepSolid';
import { createStepWriter, stepString } from './stepWriter';
import { wallBodies } from './wallBodies';

/**
 * Serializes the room's 3D model to STEP: a single product whose shape holds
 * one welded manifold solid per prism and per wall leaf, with each door or
 * window segment's solids in a child product of its own, placed at the root
 * origin (an identity transform) by a NEXT_ASSEMBLY_USAGE_OCCURRENCE so
 * importers show it as a named group.
 */
export const sceneToStep = ({ solids, walls, center }: ExportScene): string => {
  const writer = createStepWriter();
  const { entity, direction, cartesianPoint } = writer;
  const toMillimeters = toCenteredMillimeters(center);

  const applicationContext = entity(`APPLICATION_CONTEXT('core data for automotive mechanical design processes')`);
  entity(`APPLICATION_PROTOCOL_DEFINITION('international standard','automotive_design',2010,${applicationContext})`);
  const lengthUnit = entity(`(LENGTH_UNIT()NAMED_UNIT(*)SI_UNIT(.MILLI.,.METRE.))`);
  const angleUnit = entity(`(NAMED_UNIT(*)PLANE_ANGLE_UNIT()SI_UNIT($,.RADIAN.))`);
  const solidAngleUnit = entity(`(NAMED_UNIT(*)SI_UNIT($,.STERADIAN.)SOLID_ANGLE_UNIT())`);
  const uncertainty = entity(`UNCERTAINTY_MEASURE_WITH_UNIT(LENGTH_MEASURE(1.E-3),${lengthUnit},'distance_accuracy_value','')`);
  const context = entity(
    `(GEOMETRIC_REPRESENTATION_CONTEXT(3)GLOBAL_UNCERTAINTY_ASSIGNED_CONTEXT((${uncertainty}))GLOBAL_UNIT_ASSIGNED_CONTEXT((${lengthUnit},${angleUnit},${solidAngleUnit}))REPRESENTATION_CONTEXT('',''))`,
  );

  const prisms = scenePrisms(solids);
  const rootBodies = [
    ...prisms.filter(prism => !prism.folder).map(prism => stepSolid(writer, toMillimeters, prism.name, prismFaces(prism))),
    ...walls.flatMap(wall =>
      wallBodies(wall).map((faces, index, bodies) => stepSolid(writer, toMillimeters, bodies.length > 1 ? `${wall.name} ${index + 1}` : wall.name, faces))
    ),
  ];
  const folders = [...new Set(prisms.map(prism => prism.folder).filter(folder => folder !== undefined))]
    .map(folder => ({
      name: folder.name,
      bodies: prisms.filter(prism => prism.folder === folder).map(prism => stepSolid(writer, toMillimeters, prism.name, prismFaces(prism))),
    }));

  const productContext = entity(`PRODUCT_CONTEXT('',${applicationContext},'mechanical')`);
  const definitionContext = entity(`PRODUCT_DEFINITION_CONTEXT('part definition',${applicationContext},'design')`);
  const productReferences: string[] = [];
  /** A product with its own shape representation; returns its definition. */
  const productWithShape = (name: string, representation: string): string => {
    const product = entity(`PRODUCT('${stepString(name)}','${stepString(name)}','',(${productContext}))`);
    productReferences.push(product);
    const formation = entity(`PRODUCT_DEFINITION_FORMATION('','',${product})`);
    const definition = entity(`PRODUCT_DEFINITION('design','',${formation},${definitionContext})`);
    entity(`SHAPE_DEFINITION_REPRESENTATION(${entity(`PRODUCT_DEFINITION_SHAPE('','',${definition})`)},${representation})`);
    return definition;
  };

  const origin = entity(
    `AXIS2_PLACEMENT_3D('',${cartesianPoint([0, 0, 0])},${direction([0, 0, 1])},${direction([1, 0, 0])})`,
  );
  const rootRepresentation = entity(`ADVANCED_BREP_SHAPE_REPRESENTATION('floor plan',(${[origin, ...rootBodies].join(',')}),${context})`);
  const rootDefinition = productWithShape('floor-plan', rootRepresentation);
  folders.forEach((folder, folderIndex) => {
    const childOrigin = entity(
      `AXIS2_PLACEMENT_3D('',${cartesianPoint([0, 0, 0])},${direction([0, 0, 1])},${direction([1, 0, 0])})`,
    );
    const childRepresentation = entity(
      `ADVANCED_BREP_SHAPE_REPRESENTATION('${stepString(folder.name)}',(${[childOrigin, ...folder.bodies].join(',')}),${context})`,
    );
    const childDefinition = productWithShape(folder.name, childRepresentation);
    const occurrence = entity(
      `NEXT_ASSEMBLY_USAGE_OCCURRENCE('NAUO${folderIndex + 1}','${stepString(folder.name)}','',${rootDefinition},${childDefinition},$)`,
    );
    const transformation = entity(`ITEM_DEFINED_TRANSFORMATION('','',${origin},${childOrigin})`);
    const relationship = entity(
      `(REPRESENTATION_RELATIONSHIP('','',${childRepresentation},${rootRepresentation})REPRESENTATION_RELATIONSHIP_WITH_TRANSFORMATION(${transformation})SHAPE_REPRESENTATION_RELATIONSHIP())`,
    );
    entity(`CONTEXT_DEPENDENT_SHAPE_REPRESENTATION(${relationship},${entity(`PRODUCT_DEFINITION_SHAPE('','',${occurrence})`)})`);
  });
  entity(`PRODUCT_RELATED_PRODUCT_CATEGORY('part','',(${productReferences.join(',')}))`);

  return [
    'ISO-10303-21;',
    'HEADER;',
    `FILE_DESCRIPTION(('Kai floor plan'),'2;1');`,
    `FILE_NAME('floor-plan.step','',(''),(''),'','','');`,
    `FILE_SCHEMA(('AUTOMOTIVE_DESIGN { 1 0 10303 214 1 1 1 1 }'));`,
    'ENDSEC;',
    'DATA;',
    ...writer.data,
    'ENDSEC;',
    'END-ISO-10303-21;',
  ].join('\n') + '\n';
};

/** The room's 3D model as STEP, centered on its primary rectangle (the scene's own center). */
export const roomToStep = (room: FloorPlanRoom): string => sceneToStep(buildScene3D(room));
